// Camera: perspective (orbit/walk) or orthographic top-down (plan). Z up.
import { type V3, type M4, add, cross, forward, invert, mul, norm, ortho, perspective, scale, sub, viewFromBasis, rotate, clamp, dot } from './math.ts';

export type Mode = 'orbit' | 'walk' | 'plan';
const Z: V3 = [0, 0, 1];

export class Camera {
  mode: Mode = 'orbit';
  eye: V3 = [0, -10, 5];
  yaw = Math.PI / 2;
  pitch = -0.4;
  fov = (60 * Math.PI) / 180;
  near = 0.05;
  far = 400;
  // plan view: centre, metres per CSS pixel, and the world direction that is screen-up
  pc: [number, number] = [0, 0];
  ps = 0.03;
  up2: [number, number] = [0, 1];
  planTop = 20;
  planDepth = 40;
  /** The view's centre (where it looks, and orbits about), from the canvas's centre in CSS px: the View tab's middle, the canvas being the page. */
  shift: [number, number] = [0, 0];
  // derived, per frame
  w = 1; h = 1; // CSS pixels
  view!: M4; proj!: M4; vp!: M4; ivp!: M4;
  r!: V3; u!: V3; f!: V3; e!: V3;

  get ortho() { return this.mode === 'plan'; }

  update(w: number, h: number) {
    this.w = w; this.h = h;
    if (this.ortho) {
      const u: V3 = [this.up2[0], this.up2[1], 0];
      this.f = [0, 0, -1];
      this.u = u;
      this.r = [u[1], -u[0], 0];
      this.e = [this.pc[0], this.pc[1], this.planTop];
      this.proj = ortho((this.ps * w) / 2, (this.ps * h) / 2, 0, this.planDepth);
    } else {
      this.f = forward(this.yaw, this.pitch);
      this.r = norm(cross(this.f, Z));
      this.u = cross(this.r, this.f);
      this.e = this.eye;
      this.proj = perspective(this.fov, w / h, this.near, this.far);
    }
    // the centre moved by shift: x_ndc += sx, y_ndc += sy (each row 0, 1 plus that times row 3, the w)
    const sx = (2 * this.shift[0]) / w, sy = (-2 * this.shift[1]) / h;
    for (let c = 0; c < 4; c++) { this.proj[c * 4] += sx * this.proj[c * 4 + 3]; this.proj[c * 4 + 1] += sy * this.proj[c * 4 + 3]; }
    this.view = viewFromBasis(this.r, this.u, this.f, this.e);
    this.vp = mul(this.proj, this.view);
    this.ivp = invert(this.vp);
  }

  /** Pixels per metre at view depth 1 (perspective) or per metre (ortho), in CSS px. */
  get pxPerM() {
    return this.ortho ? 1 / this.ps : this.h / 2 / Math.tan(this.fov / 2);
  }

  /** The view's centre in CSS px. */
  get centre(): [number, number] { return [this.w / 2 + this.shift[0], this.h / 2 + this.shift[1]]; }

  /** World ray through CSS pixel (x, y). */
  ray(x: number, y: number): { o: V3; d: V3 } {
    const nx = ((x - this.shift[0]) / this.w) * 2 - 1, ny = 1 - ((y - this.shift[1]) / this.h) * 2;
    if (this.ortho) {
      const o = add(this.e, add(scale(this.r, (nx * this.ps * this.w) / 2), scale(this.u, (ny * this.ps * this.h) / 2)));
      return { o, d: this.f };
    }
    const t = Math.tan(this.fov / 2), a = this.w / this.h;
    return { o: this.eye, d: norm(add(this.f, add(scale(this.r, nx * t * a), scale(this.u, ny * t)))) };
  }

  /** World point from CSS pixel and view depth (distance along the forward axis). */
  unproject(x: number, y: number, depth: number): V3 {
    const { o, d } = this.ray(x, y);
    return add(o, scale(d, this.ortho ? depth : depth / dot(d, this.f)));
  }

  /** Orbit about pivot by yaw/pitch deltas; the pivot stays put on screen. */
  orbit(pivot: V3, dyaw: number, dpitch: number) {
    const np = clamp(this.pitch + dpitch, -1.55, 1.55);
    dpitch = np - this.pitch;
    let v = sub(this.eye, pivot);
    v = rotate(v, Z, dyaw);
    const r = norm(cross(forward(this.yaw + dyaw, this.pitch), Z));
    v = rotate(v, r, dpitch);
    this.eye = add(pivot, v);
    this.yaw += dyaw;
    this.pitch = np;
  }

  /** Move in the screen plane by CSS pixels, at a given depth. */
  pan(dx: number, dy: number, depth: number) {
    if (this.ortho) {
      this.pc = [this.pc[0] - (this.r[0] * dx - this.u[0] * dy) * this.ps, this.pc[1] - (this.r[1] * dx - this.u[1] * dy) * this.ps];
      return;
    }
    const k = (2 * Math.tan(this.fov / 2) * depth) / this.h;
    this.eye = add(this.eye, add(scale(this.r, -dx * k), scale(this.u, dy * k)));
  }

  /** Plan zoom by factor about CSS pixel (x, y). */
  zoomPlan(f: number, x: number, y: number) {
    const before = this.unproject(x, y, 0);
    this.ps = clamp(this.ps * f, 0.0005, 2);
    this.update(this.w, this.h);
    const after = this.unproject(x, y, 0);
    this.pc = [this.pc[0] + before[0] - after[0], this.pc[1] + before[1] - after[1]];
  }

  lookAt(target: V3) {
    const d = norm(sub(target, this.eye));
    this.yaw = Math.atan2(d[1], d[0]);
    this.pitch = Math.asin(clamp(d[2], -1, 1));
  }
}
